import React, { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { PageTitleRow } from '@/components/ui/page-title-row';
import { useHouseholdRouteType } from '@/utils';
import { useTenantScopeLabel } from '@/views/auth/tenant-selection';
import { Text } from 'widgets';
import { HorizontalLayout, VerticalLayout } from 'widgets/Layout';

import { GlobalSearchTermInput } from './GlobalSearchTermInput';

interface FilterFormLayoutProps {
    centerContent?: ReactNode;
    count?: number;
    countLabel?: string;
    filterButtons: ReactNode;
    filterLabels: ReactNode;
    isFilterActive: boolean;
    onResetFilters: () => void;
    rightActions?: ReactNode;
    showResetFilters?: boolean;
}

export const FilterFormLayout = ({
    centerContent,
    count,
    countLabel,
    filterButtons,
    filterLabels,
    isFilterActive,
    onResetFilters,
    rightActions,
    showResetFilters = true,
}: FilterFormLayoutProps) => {
    const { isHouseholdRoute } = useHouseholdRouteType();
    const tenantScopeLabel = useTenantScopeLabel();
    const countText =
        count !== undefined && countLabel !== undefined
            ? `${count} ${countLabel}${tenantScopeLabel ? ` · ${tenantScopeLabel}` : ''}`
            : null;

    const controls = (
        <HorizontalLayout $gap={8} $rowStart $alignItems="center">
            {centerContent ? <GlobalSearchTermInput placeholder="Filter table ..." round /> : null}
            {isHouseholdRoute && (
                <>
                    {filterButtons}
                    {isFilterActive && showResetFilters ? (
                        <Button variant="ghost" onClick={onResetFilters}>
                            Reset
                        </Button>
                    ) : null}
                </>
            )}
            {!centerContent ? <GlobalSearchTermInput placeholder="Filter table ..." round /> : null}
            {!centerContent && countText && (
                <Text helper $themeColor="subtle" className="text-cocoa-600 !mt-0 flex h-9 items-center pr-3 font-thin">
                    {countText}
                </Text>
            )}
            {rightActions ? <div className="flex gap-2 pl-4">{rightActions}</div> : null}
        </HorizontalLayout>
    );

    return (
        <div className="flex flex-col">
            <PageTitleRow
                actions={
                    centerContent ? (
                        controls
                    ) : rightActions ? (
                        <HorizontalLayout $gap={8} $rowCenter>
                            {rightActions}
                        </HorizontalLayout>
                    ) : null
                }
            >
                {centerContent ? (
                    <VerticalLayout $gap={0}>
                        <div className="flex items-center">{centerContent}</div>
                        {countText ? (
                            <Text helper $themeColor="subtle" className="text-cocoa-600 !-mt-1 font-thin">
                                {countText}
                            </Text>
                        ) : null}
                    </VerticalLayout>
                ) : (
                    controls
                )}
            </PageTitleRow>
            {isFilterActive && filterLabels ? <div className="px-6 pb-4">{filterLabels}</div> : null}
        </div>
    );
};
